import React from 'react'
import { connect } from 'react-redux'
import { OmSpinner, omMessage } from '@owma/kit'

import i18n from 'i18n'
import classNames from 'classnames'
import conf from 'conf'

import { ACTION, TYPE } from 'pages/main/data/defConstants'
import { getMessageId } from 'pages/main/data/JMAPMailbox'
import { ServerRecoveryController } from '../serverRecoveryHelper'

import dataManager from 'pages/main/data/DataManager'
import emailHelper from '../emailHelper'

import syncAniGIF from 'assets/images/gif/loading.gif'

import { useLocalThreadViewer } from 'vars'
import { detachIFrameImages } from 'utils'

import { initViewerApi, disposeViewerApi } from '../viewerExt'
import type { ViewerWindow } from '@owma/common-web/components/viewer'
import {
  attachReactMgr as attachReactMgrShared,
  detachReactMgr as detachReactMgrShared,
  extractInviteAddressList as extractInviteAddressListShared,
  handleCreateEvent as handleCreateEventShared,
  handleShowCalendar as handleShowCalendarShared,
  handleCopyInvitation as handleCopyInvitationShared,
  onCreateInvitation as onCreateInvitationShared,
  getJMAPMessageId as getJMAPMessageIdShared,
} from '../threadViewShared'
import type { ThreadViewApi } from '../threadViewApi'

import styles from '../style.module.scss'
import moment from 'moment'

import { OutlookAvailabilityTime } from 'pages/calendar/modules/outlookdatatime/OutlookAvailabilityTime'

import { modalEventEditor } from 'pages/calendar'

interface ThreadViewProps {
  innerRef?: any
  email?: any
  emails?: any[]
  curEmail?: any
  curMailbox?: any
  dateFormat?: string
  timeFormat?: string
  isUnknownAlwaysShowExternalResources?: boolean
  isKnownAlwaysShowExternalResources?: boolean
  user?: any
  runCommand?: (cmd: string, messages?: any[] | null, forced?: boolean, param2?: any) => void
  openSendMail?: (opts?: any) => void
  replyOrForwardEmail?: (cmd: string, email: any) => void
  deleteDraft?: (email: any) => void
  byViewer: boolean // viewer에서 시작된 동작으로 변경이 일어난 경우인지 확인.
}

interface ThreadViewState {
  showLoadingAni: boolean
  isLoading: boolean
  invitationMenuVisible: boolean
  invitationData: any
}

const urlInfo: any = {}
const { origin } = window.location

const hash = conf.gitInfo.commit.hash

if (useLocalThreadViewer) {
  // urlInfo.src = origin + '/viewer/index.html?hash=' + hash
  urlInfo.src = origin + '/plain/threadViewer'
} else {
  urlInfo.src = origin + '/viewer/index.html?hash=' + hash
}

const mapStateToProps = ({ settings, user }: any) => ({
  isUnknownAlwaysShowExternalResources: settings.email.isUnknownAlwaysShowExternalResources,
  isKnownAlwaysShowExternalResources: settings.email.isKnownAlwaysShowExternalResources,
  dateFormat: settings.general?.dateFormat,
  timeFormat: settings.general?.timeFormat,
  user,
})

export class ThreadView
  extends React.Component<ThreadViewProps, ThreadViewState>
  implements ThreadViewApi
{
  state: ThreadViewState = {
    showLoadingAni: false, // 최초 로딩 시 사용.
    isLoading: false, // 로딩 중인지 확인.
    invitationMenuVisible: false,
    invitationData: null,
  }

  mounted = false
  loaded = false

  refObj: any = null
  iframe: any = null
  observerObj: any = null
  emailInfoMap: any = {}
  searchTimer: any = null

  // 서버 복구 시 본문 미로딩 메일 자동 재요청 (OFM-214). viewer window 는 iframe.contentWindow.
  private serverRecoveryCtrl = new ServerRecoveryController({
    getMounted: () => this.mounted,
    getEmailInfoMap: () => this.emailInfoMap,
    getViewerWindow: () => (this.iframe != null ? this.iframe.contentWindow : null),
  })

  public applyThreadFunc?: () => void = undefined
  public isFirstSet: boolean = false
  public isThreadMode: boolean = false
  public prevThreadInfo?: {
    emailIds: string[]
    curEmailId: string | null
    curThreadId: string | null
  } = undefined
  public loadingTimer: string | number | NodeJS.Timeout | null = null
  public loadingTimerRetureFunc: string | null = null
  public delayShowTimer: string | number | NodeJS.Timeout | null = null
  public loadingAniTimer: NodeJS.Timeout | null = null
  public notiCheckTimers: Set<NodeJS.Timeout> = new Set()
  // 이 인스턴스가 등록한 window.initNative 참조 — 다중 마운트 경쟁에서 자기 것만 해제하기 위함.
  public ownInitNative?: () => void = undefined

  componentDidMount() {
    const { innerRef } = this.props

    ;(dataManager as any).addEmailChangeListener(this.onEmailChanged)

    this.refObj = this.createRefObj()

    if (innerRef != null) {
      if (typeof innerRef === 'function') {
        innerRef(this.refObj)
      } else {
        innerRef.current = this.refObj
      }
    }

    i18n.on('languageChanged', this.onLanguageChanged)

    this.checkMounted()

    this.attachReactMgr(this.props)

    // 서버 복구 시 viewer 에 표시 중이며 본문 미로딩인 메일의 body 를 자동 재요청한다.
    this.serverRecoveryCtrl.register()

    this.showLoadingAni(5000)
  }

  componentDidUpdate(prevProps: ThreadViewProps) {
    const { curEmail, email, emails, curMailbox, dateFormat, timeFormat } = this.props

    if (prevProps != null) {
      if (
        prevProps.email !== email ||
        prevProps.emails !== emails ||
        prevProps.curMailbox !== curMailbox
      ) {
        this.applyThread()

        this.detachReactMgr(prevProps)
        this.attachReactMgr(this.props)
      }
      if (
        (prevProps.curEmail == null && curEmail != null) ||
        (prevProps.curEmail != null && curEmail == null) ||
        (prevProps.curEmail != null &&
          curEmail != null &&
          prevProps.curEmail.data.id !== curEmail.data.id)
      ) {
        this.applyCurEmail()
      }
      if (prevProps.dateFormat !== dateFormat || prevProps.timeFormat !== timeFormat) {
        this.applyDateTimeFormat()
      }
    }
  }

  componentWillUnmount() {
    const { innerRef } = this.props

    this.mounted = false

    if (innerRef != null) {
      if (typeof innerRef !== 'function') {
        innerRef.current = null
      }
    }

    ;(dataManager as any).removeEmailChangeListener(this.onEmailChanged)
    i18n.off('languageChanged', this.onLanguageChanged)
    this.serverRecoveryCtrl.unregister()

    // 보류 중인 타이머 정리 — unmount 후 setState/stale viewer 호출 방지.
    if (this.loadingTimer != null) {
      clearTimeout(this.loadingTimer)
      this.loadingTimer = null
      this.loadingTimerRetureFunc = null
    }
    if (this.delayShowTimer != null) {
      clearTimeout(this.delayShowTimer)
      this.delayShowTimer = null
    }
    if (this.loadingAniTimer != null) {
      clearTimeout(this.loadingAniTimer)
      this.loadingAniTimer = null
    }
    this.notiCheckTimers.forEach((timer) => clearTimeout(timer))
    this.notiCheckTimers.clear()

    if (this.observerObj != null) {
      this.observerObj.clear()
      // detached iframe body/element가 인스턴스에 잔존하지 않도록 참조 해제.
      this.observerObj.element = null
      this.observerObj.ref = null
    }

    this.detachReactMgr(this.props)

    // 전역(window.initNative, threadViewInfo)이 detached iframe window를
    // 붙잡지 않도록 해제하고, 인스턴스의 노드/데이터 참조도 끊는다.
    // ownInitNative를 넘겨 다중 마운트 경쟁에서 자기 등록만 정확히 해제.
    disposeViewerApi(this.ownInitNative)
    this.ownInitNative = undefined
    this.applyThreadFunc = undefined
    this.prevThreadInfo = undefined
    this.emailInfoMap = {}

    // detached 뷰어 문서가 GC되도록: ① contentDocument img 분리(로드 참조 차단)
    // ② iframe→host 역참조(NineNative) 해제 ③ about:blank로 문서 폐기 유도.
    // 셋 다 this.iframe이 살아있을 때 수행해야 하므로 null 대입 전에 처리한다.
    detachIFrameImages(this.iframe)
    try {
      const viewerWindow: ViewerWindow | null =
        this.iframe != null ? this.iframe.contentWindow : null
      if (viewerWindow != null && viewerWindow.NineNative != null) {
        viewerWindow.NineNative = undefined
      }
    } catch (e) {
      console.warn('[IFrameViewer] NineNative 해제 실패:', e)
    }
    try {
      if (this.iframe != null) {
        // about:blank 대입이 미발화 onload 핸들러를 트리거하지 않도록 먼저 제거.
        this.iframe.onload = null
        this.iframe.src = 'about:blank'
      }
    } catch (e) {
      console.warn('[IFrameViewer] iframe about:blank 실패:', e)
    }

    this.iframe = null
    this.refObj = null
  }

  createRefObj = () => {
    const obj = {
      setFindText: (text: string) => {
        const viewerWindow: ViewerWindow | null =
          this.iframe != null ? this.iframe.contentWindow : null
        if (viewerWindow != null && viewerWindow.webObj != null) {
          viewerWindow.webObj.setFindText(text)
        }
      },
      printEmail: (email: any) => {
        // OMD-194: 인쇄는 host 가 오케스트레이션. NineNative.print 가 뷰어에서 payload 를
        // 되가져와(fold 처리 포함) 인쇄한다.
        const viewerWindow: ViewerWindow | null =
          this.iframe != null ? this.iframe.contentWindow : null
        if (viewerWindow != null && viewerWindow.NineNative != null && email != null) {
          viewerWindow.NineNative.print?.(email.getId())
        }
      },
      setHideEmptyIcon: (hideEmptyIcon: boolean) => {
        const viewerWindow: ViewerWindow | null =
          this.iframe != null ? this.iframe.contentWindow : null
        if (viewerWindow != null && viewerWindow.webObj != null) {
          viewerWindow.webObj.setHideEmptyIcon(hideEmptyIcon)
        }
      },
      // 에디터 탭 활성화 중 iframe 뷰어 bake 를 일시정지해 메인 스레드 경합을 없앤다. 'view' 탭
      // 복귀 시 재개(host 의 setTabsActiveKey 가 토글). webObj.setBakePaused 는 iframe 모듈의
      // bakePauseGate 싱글턴을 토글한다.
      setBakePaused: (paused: boolean) => {
        const viewerWindow: ViewerWindow | null =
          this.iframe != null ? this.iframe.contentWindow : null
        if (viewerWindow != null && viewerWindow.webObj != null) {
          viewerWindow.webObj.setBakePaused(paused)
        }
      },
      reload: () => {
        const viewerWindow: ViewerWindow | null =
          this.iframe != null ? this.iframe.contentWindow : null
        if (viewerWindow != null) {
          viewerWindow.location.reload()
        }
      },
    }

    return obj
  }

  attachReactMgr = (props: ThreadViewProps): void => {
    attachReactMgrShared(props, this)
  }

  detachReactMgr = (props: ThreadViewProps): void => {
    detachReactMgrShared(props, this)
  }

  // updateState = () => {
  //   this.applyThread()
  // }

  showLoadingAni = (delay = 200) => {
    this.setState({
      showLoadingAni: true,
    })
    if (this.loadingAniTimer != null) {
      clearTimeout(this.loadingAniTimer)
      this.loadingAniTimer = null
    }
    if (delay > 0) {
      this.loadingAniTimer = setTimeout(() => {
        this.loadingAniTimer = null
        this.closeLoadingAni()
      }, delay)
    }
  }

  closeLoadingAni = () => {
    this.setState({
      showLoadingAni: false,
    })
  }

  setNormalLoading = (loading: boolean) => {
    this.setState({
      isLoading: loading,
    })
  }

  onLanguageChanged = (locale: string) => {
    // i18n.off 직후 큐에 남은 이벤트가 unmount 후 발화할 수 있어 iframe null 가드.
    const viewerWindow: ViewerWindow | null = this.iframe != null ? this.iframe.contentWindow : null
    if (viewerWindow != null && viewerWindow.webObj != null) {
      viewerWindow.webObj.setLanguage(locale)

      this.applyEmailChanged()
    }
  }

  createEmailInfo = (email: any) => {
    const { curMailbox } = this.props

    const isOutbox = curMailbox != null && curMailbox.getRole() === 'outbox'

    const emailId = email.data.id
    this.emailInfoMap[emailId] = emailHelper.createEmailInfoFromEmail(
      email,
      isOutbox,
      curMailbox,
      this.isThreadMode === true,
    )

    return this.emailInfoMap[emailId]
  }

  extractInviteAddressList = (
    accountEmail: string,
    from: any[],
    to: any[],
    cc: any[],
  ): ReturnType<typeof extractInviteAddressListShared> => {
    return extractInviteAddressListShared(accountEmail, from, to, cc)
  }

  onCreateInvitation = (startDate: string, endDate: string, _subject: string): void => {
    onCreateInvitationShared(
      OutlookAvailabilityTime.outlookAvailabilityTime2Calendar,
      modalEventEditor as (
        start: unknown,
        end: unknown,
        subject: string,
        required: unknown,
        optional: unknown,
      ) => void,
      this.props,
      startDate,
      endDate,
    )
  }

  handleInvitationMenuClick = (key: string) => {
    const { invitationData } = this.state

    if (!invitationData) return

    switch (key) {
      case 'createEvent':
        this.handleCreateEvent(invitationData)
        break
      case 'showCalendar':
        this.handleShowCalendar(invitationData)
        break
      case 'copy':
        this.handleCopyInvitation(invitationData)
        break
      default:
        break
    }

    // Close menu after selection
    this.setState({ invitationMenuVisible: false })
  }

  handleCreateEvent = (invitationData: any): void => {
    handleCreateEventShared(
      modalEventEditor as (
        start: unknown,
        end: unknown,
        subject: string,
        required: unknown,
        optional: unknown,
      ) => void,
      invitationData,
    )
  }

  handleShowCalendar = (invitationData: any): void => {
    handleShowCalendarShared(moment, invitationData)
  }

  handleCopyInvitation = (invitationData: any): void => {
    handleCopyInvitationShared(omMessage, i18n.t.bind(i18n), invitationData)
  }

  handleInvitationMenuVisibleChange = (visible: boolean) => {
    this.setState({ invitationMenuVisible: visible })
  }

  onEmailChanged = (evt: any) => {
    const { email, emails } = this.props

    // 표시되고 있는 이메일이 없으면 종료.
    if (email == null && emails == null) {
      return
    }

    // flag, read 상태 변경 시 호출.
    if (evt.type === TYPE.EMAIL && (evt.action === ACTION.UPDATE || evt.action === ACTION.MODIFY)) {
      const emailList = emails != null ? emails : [email]

      emailList.forEach((item: any) => {
        let existItem = null

        const { messageId } = evt.data
        if (messageId != null && item.data.messageId != null) {
          const msgId = getMessageId(item.data.messageId[0])
          if (msgId === messageId) {
            existItem = item
          }
        }

        if (existItem == null) {
          existItem = evt.data.list.find((evtItem: any) => {
            return evtItem.data.id === item.data.id
          })
        }

        if (existItem != null) {
          const viewerWindow: ViewerWindow | null =
            this.iframe != null ? this.iframe.contentWindow : null
          if (viewerWindow != null && viewerWindow.webObj != null && this.emailInfoMap != null) {
            let emailInfo = this.emailInfoMap[existItem.getId()]
            let changedData: any = {}
            if (emailInfo == null) {
              emailInfo = this.createEmailInfo(existItem)
              changedData = emailInfo.dataForJSON
            } else if (emailInfo.dataForJSON.isLoaded === false && existItem.isLoaded === true) {
              emailInfo.generateData()
              changedData = emailInfo.dataForJSON
            } else {
              emailInfo.generateData()
              changedData = emailInfo.dataForJSON
              // const curData = emailInfo.dataForJSON
              // changedData = {
              //   summary: curData.summary,
              //   flagged: curData.flagged,
              //   draft: curData.draft,
              //   read: curData.read,
              //   received_time: curData.received_time,
              // }
            }

            const changedDataStr = JSON.stringify(changedData)

            let notiCnt = 0
            const notiCheck = () => {
              // unmount 후에는 stale viewerWindow를 호출하지 않는다.
              if (this.mounted === false) {
                return
              }
              const emailIds = viewerWindow.webObj.getEmailIds()
              if (emailIds.indexOf(existItem.getId()) < 0 && notiCnt < 20) {
                const timer = setTimeout(() => {
                  this.notiCheckTimers.delete(timer)
                  notiCheck()
                }, 100)
                this.notiCheckTimers.add(timer)
                notiCnt += 1
              } else {
                viewerWindow.webObj.notifyEmailChanged(existItem.data.id, changedDataStr)
              }
            }
            notiCheck()
          }
        }
      })
    }
  }

  applyThread = () => {
    if (this.applyThreadFunc != null) {
      this.applyThreadFunc()
    }
  }

  applyEmailChanged = () => {
    const { emails } = this.props

    if (emails != null && emails.length > 0) {
      emails.forEach((item: any) => {
        const emailId = item.data.id
        const emailInfo = this.emailInfoMap[emailId]
        if (emailInfo != null) {
          const viewerWindow: ViewerWindow | null =
            this.iframe != null ? this.iframe.contentWindow : null
          if (viewerWindow != null && viewerWindow.webObj != null) {
            // 언어 변경 시 시스템 폴더명 등 메일함 표시 이름이 재지역화되므로,
            // dataForJSON 을 재생성해 최신 mailbox_name/mailbox_fullname 을 함께 전달한다.
            emailInfo.generateData()
            const { mailbox_name, mailbox_fullname } = emailInfo.dataForJSON
            const meetingInfo = emailInfo.getMeetingInfo()
            const changedData = {
              mailbox_name,
              mailbox_fullname,
              ...(meetingInfo ?? {}),
            }
            viewerWindow.webObj.notifyEmailChanged(emailId, JSON.stringify(changedData))
          }
        }
      })
    }
  }

  getJMAPMessageId = (email: any): string => {
    return getJMAPMessageIdShared(email)
  }

  applyDateTimeFormat = () => {
    const { dateFormat, timeFormat } = this.props

    const viewerWindow: ViewerWindow | null = this.iframe != null ? this.iframe.contentWindow : null
    if (viewerWindow != null && viewerWindow.webObj != null) {
      const settings = {
        dateFormat,
        timeFormat,
      }
      const settingsStr = JSON.stringify(settings)
      viewerWindow.webObj.setSettings(settingsStr, true)
    }
  }

  applyCurEmail = () => {
    const { curEmail, byViewer } = this.props

    console.log(' --- applyCurEmail : ', curEmail)

    const viewerWindow: ViewerWindow | null = this.iframe.contentWindow
    if (viewerWindow != null && viewerWindow.webObj != null) {
      // const messageId = this.getJMAPMessageId(curEmail)
      if (curEmail != null) {
        const emailId = curEmail.data.id
        viewerWindow.webObj.select(emailId, false, byViewer)
      }
    }
  }

  checkMounted = () => {
    this.mounted = true

    if (this.loaded === true && this.mounted === true && this.iframe != null) {
      this.onCheckEnded()
    }
  }

  checkLoaded = () => {
    this.loaded = true

    if (this.loaded === true && this.mounted === true && this.iframe != null) {
      this.onCheckEnded()
    }
  }

  onCheckEnded = () => {
    const viewerWindow = this.iframe.contentWindow
    if ((window as any).initNative != null && viewerWindow.NineNative == null) {
      ;(window as any).initNative()
    }
  }

  onReady = () => {
    this.initRef(this.iframe)
  }

  initRef = (ref: any) => {
    if (this.observerObj == null) {
      this.observerObj = {}

      this.observerObj.calcHeight = () => {
        if (this.observerObj.resizeTimer != null) {
          cancelAnimationFrame(this.observerObj.resizeTimer)
        }
        this.observerObj.resizeTimer = requestAnimationFrame(() => {
          if (
            this.observerObj.element != null &&
            this.observerObj.ref.contentDocument.documentElement != null
          ) {
            this.observerObj.element.style.overflow = 'hidden'
            // const the_height = this.observerObj.element.scrollHeight
            const the_height = this.observerObj.ref.contentDocument.documentElement.scrollHeight
            if (String(the_height) !== this.observerObj.ref.height) {
              console.log(' --- resize height : ', the_height)
              this.observerObj.ref.height = String(the_height)
            }
            this.observerObj.resizeTimer = null
          }
        })
      }

      this.observerObj.observer = new ResizeObserver(() => {
        this.observerObj.calcHeight()
      })

      this.observerObj.clear = () => {
        const { observer, resizeTimer } = this.observerObj

        if (observer != null) {
          observer.disconnect()
          if (resizeTimer != null) {
            cancelAnimationFrame(resizeTimer)
          }
        }
      }
    }

    if (ref != null && this.observerObj.ref != ref) {
      this.observerObj.clear()

      this.observerObj.ref = ref

      const startCheckHeight = () => {
        // if (this.observerObj.element != null) {
        //   this.observerObj.calcHeight()
        //   this.observerObj.observer.observe(this.observerObj.element)
        // }
      }

      if (ref.contentDocument != null) {
        console.log(
          ' -- contentDocument - ',
          ref.contentWindow.location,
          ref.contentDocument.readyState,
          ref.contentDocument.body,
        )
        if (
          ref.contentDocument.readyState === 'complete' ||
          ref.contentDocument.readyState === 'interactive'
        ) {
          this.observerObj.element = ref.contentDocument.body
          startCheckHeight()
        } else {
          ref.contentDocument.addEventListener('DOMContentLoaded', () => {
            this.observerObj.element = ref.contentDocument.body
            startCheckHeight()
          })
        }
      } else {
        ref.contentWindow.addEventListener('load', () => {
          this.observerObj.element = ref.contentDocument.body
          startCheckHeight()
        })
      }
    }
  }

  setIFrame = (ref: any) => {
    if (ref != null && this.iframe !== ref) {
      console.log(' -- setIFrame - ', ref)
      this.iframe = ref

      const viewerWindow = this.iframe.contentWindow
      if (viewerWindow != null) {
        initViewerApi(viewerWindow, this)
        this.checkLoaded()
      } else {
        this.iframe.onload = () => {
          const viewerWindow = this.iframe.contentWindow
          initViewerApi(viewerWindow, this)
          this.checkLoaded()

          this.iframe.onload = null
        }
      }
    }
  }

  render() {
    const { showLoadingAni, isLoading /*, invitationMenuVisible */ } = this.state

    // const invitationMenuItems = [
    //   {
    //     label: i18n.t('calendar.invitationMenu.createEvent') || 'Create an event',
    //     key: 'createEvent',
    //   },
    //   {
    //     label: i18n.t('calendar.invitationMenu.showCalendar') || 'Show calendar',
    //     key: 'showCalendar',
    //   },
    //   {
    //     label: i18n.t('calendar.invitationMenu.copy') || 'Copy',
    //     key: 'copy',
    //   },
    // ]

    return (
      <div className={classNames(styles.threadView, 'threadView')}>
        <OmSpinner spinning={isLoading}>
          <iframe
            key={urlInfo.src}
            className={classNames(styles.mailBody)}
            src={urlInfo.src}
            data-testid="mail-body"
            // Sentry Session Replay(rrweb)가 이 iframe 내부 메일 본문 DOM까지 재귀 직렬화하면서
            // 오래 열어둘수록 로딩 지연이 폭증한다. 본문 영역을 replay 기록에서 제외한다.
            data-sentry-block
            ref={(ref) => {
              this.setIFrame(ref)
            }}
          />
        </OmSpinner>
        <div className={classNames(styles.loadingAni, { show: showLoadingAni })}>
          <img src={syncAniGIF} alt="sync" />
        </div>
        {/* <Drawer
          title={null}
          placement="bottom"
          closable={false}
          onClose={() => this.handleInvitationMenuVisibleChange(false)}
          open={invitationMenuVisible}
          getContainer={false}
          height="auto"
          mask={true}
          maskClosable={true}
          className={classNames(styles.invitationDrawer)}
        >
          <div className={classNames(styles.invitationMenuContainer)}>
            {invitationMenuItems.map((item) => (
              <div
                key={item.key}
                className={classNames(styles.invitationMenuItem)}
                onClick={() => this.handleInvitationMenuClick(item.key)}
              >
                {item.label}
              </div>
            ))}
          </div>
        </Drawer> */}
      </div>
    )
  }
}

export default connect(mapStateToProps)(ThreadView)
